import React, { useState, useEffect } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  useDisclosure,
  FormControl,
  FormLabel,
  Box,
  ModalCloseButton,
} from '@chakra-ui/react';
import Select from 'react-select';
import fetchWrapper from '../lib/fetch-wrapper';
import { useProfile } from '../lib/profileContext';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { MdAddBox, MdDelete, MdEdit } from 'react-icons/md';
import MultiSelect from '../components/multiSelect';

const placeholderOptions = [
  { value: '', label: 'No Distro' },
  { value: 'hr_distro', label: 'HR' },
  { value: 'benefits_distro', label: 'Benefits' }
];

const FAQPage = () => {
  const { instance, accounts }: { instance: IPublicClientApplication; accounts: AccountInfo[] } = useMsal();
  const { profile } = useProfile();
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord]: any = useState(null);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [formData, setFormData]: any = useState({ Question: '', Answer: '', Property: [], Placeholder: '' });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    void fetchRecords();
    void fetchProperties();
  }, []);

  const fetchRecords = async () => {
    try {
      const fetchInstance = new fetchWrapper();
      const response = await fetchInstance.fetchRecords('/api/faq/records');
      setRecords(response.data.records);
    } catch (error) {
      toast({ title: 'Error fetching FAQ', status: 'error', duration: 3000 });
    }
  };

  const fetchProperties = async () => {
    try {
      const fetchInstance = new fetchWrapper();
      const response = await fetchInstance.getProperties();
      const formatted = response.data.records.map((r: any) => ({ value: r.data.Name, label: r.data.Name }));
      setPropertyOptions(formatted);
    } catch (err) {
      toast({ title: 'Error loading properties', status: 'error', duration: 3000 });
    }
  };

  const deleteRecord = async (rowKey: any) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      const fetchInstance = new fetchWrapper();
      await fetchInstance.deleteRecord(`/api/faq/remove/${rowKey}`);
      toast({ title: 'FAQ deleted', status: 'success', duration: 3000 });
      void fetchRecords();
    } catch (error) {
      toast({ title: 'Error deleting FAQ', status: 'error', duration: 3000 });
    }
  };

  const handleEdit = (record: any) => {
    const timestamp = new Date(record.data.Timestamp).toLocaleString();
    setSelectedRecord(record);
    setFormData({
      RowKey: record.RowKey,
      ...record.data,
      Timestamp: timestamp,
      Property: (record.data.Property || '')
          .split(',')
          .map((p: string) => p.trim())
          .filter((p: string) => p),
    });
    onOpen();
  };

  const handleAdd = () => {
    setSelectedRecord(null);
    setFormData({ Question: '', Answer: '', Property: [], Placeholder: '' });
    onOpen();
  };

  const handlePlaceholderChange = (selectedOption: any) => {
    let newAnswer = formData.Answer;

    newAnswer = newAnswer.replace(/<<<hr_distro>>>|<<<benefits_distro>>>/g, '').trim();

    if (selectedOption.value) {
      newAnswer += ` <<<${selectedOption.value}>>>`;
    }

    setFormData({ ...formData, Answer: newAnswer, Placeholder: selectedOption.value });
  };

  const handlePropertiesChange = (selectedOptions: any[]) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    setFormData({
      ...formData,
      Property: selectedValues.includes('ALL') ? ['ALL'] : selectedValues.filter((v) => v !== 'ALL'),
    });
  };

  const handleSubmit = async () => {
    if (!formData.Question.trim() || !formData.Answer.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Question and Answer fields are required.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const fetchInstance = new fetchWrapper();
      const updatedFormData = {
        ...formData,
        Property: formData.Property.join(','),
      };

      if (selectedRecord) {
        await fetchInstance.editRecord('/api/faq/edit', { RowKey: selectedRecord.RowKey, data: updatedFormData });
        toast({ title: 'FAQ updated', status: 'success', duration: 3000 });
      } else {
        await fetchInstance.addRecord('/api/faq/add', updatedFormData);
        toast({ title: 'FAQ added', status: 'success', duration: 3000 });
      }

      void fetchRecords();
      onClose();
    } catch (error) {
      toast({ title: 'Error saving FAQ', status: 'error', duration: 3000 });
    }
  };

  return (
      <Box mx="auto" mt="6" bg="#ffffff" height="100%" m="0" px="30px" pt="30px" overflowY="auto">
        <Button
            variant="brand"
            size="lg"
            height="4rem"
            leftIcon={<MdAddBox size="1.5em" />}
            onClick={handleAdd}
        >
          Add FAQ
        </Button>
        <Table
            variant="simple"
            mt="4"
        >
          <Thead>
            <Tr>
              <Th>Question</Th>
              <Th>Answer</Th>
              <Th>Property</Th>
              <Th>Placeholder</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {records.map((record: any) => (
                <Tr key={record.RowKey}>
                  <Td>{record.data.Question}</Td>
                  <Td>{record.data.Answer}</Td>
                  <Td>{record.data.Property}</Td>
                  <Td>{record.data.Placeholder}</Td>
                  <Td>
                    <Button bg="transparent" leftIcon={<MdEdit color="black"/>} mr={2} onClick={() => handleEdit(record)} />
                    <Button bg="transparent" leftIcon={<MdDelete color="black"/>} onClick={() => deleteRecord(record.RowKey)} />
                  </Td>
                </Tr>
            ))}
          </Tbody>
        </Table>

        <Modal isOpen={isOpen} size="2xl" closeOnOverlayClick={false} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{selectedRecord ? 'Edit FAQ' : 'Add FAQ'}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl isRequired>
                <FormLabel>Question</FormLabel>
                <Textarea placeholder='Enter Question' value={formData.Question} onChange={(e) => setFormData({ ...formData, Question: e.target.value })} />
              </FormControl>
              <FormControl isRequired mt={2}>
                <FormLabel>Answer</FormLabel>
                <Textarea placeholder='Enter Answer' value={formData.Answer} onChange={(e) => setFormData({ ...formData, Answer: e.target.value })} />
              </FormControl>
              <FormControl mt={2}>
                <FormLabel>Properties</FormLabel>
                <MultiSelect
                    options={[{ value: 'ALL', label: 'ALL' }, ...propertyOptions]}
                    value={formData.Property.map((p: string) => ({ value: p, label: p }))}
                    onChange={handlePropertiesChange}
                />
              </FormControl>
              <FormControl mt={2}>
                <FormLabel>Placeholder</FormLabel>
                <Select options={placeholderOptions} value={placeholderOptions.find(option => option.value === formData.Placeholder)} onChange={handlePlaceholderChange} />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="cancel" onClick={onClose}>Cancel</Button>
              <Button variant="brand" ml={3} onClick={handleSubmit}>{selectedRecord ? 'Update' : 'Add'}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
  );
};

export default FAQPage;
